import { useEffect, useMemo, useState } from 'react'
import { Button, FlexBox, getTokenCssVar, useScreenClass } from '@vp/swan'
import { excludedPageSectionsForVatBar, vatBarData, vatBarEnabledLocales } from '../../constants/vat-selection'
import { COOKIE_KEYS, LOCAL_STORAGE_KEYS, STORAGE_TTL } from '../../constants/storage-keys'
import { getLocalStorageWithExpiry, setLocalStorageWithExpiry } from '../../utils/local-storage'
import { useVatContext } from '../../context/VatContext'
import { getCookie, setCookie } from '../../utils/cookies'
import { getPageInfo } from '../../utils/page-meta'
import { isCookieConsentPending } from '@vp/cookie-consent-client-reader'
import { getHeaderTracker } from '../../tracking'

const tracker = {
  trackView(locale: string) {
    getHeaderTracker().trackFlyOutView('VAT Selection Fly-Out', `view_${locale}`)
  },
  trackSelect(isVatInc: boolean) {
    getHeaderTracker().trackFlyOutClick('VAT Selection Fly-Out', `select_${isVatInc ? 'VATinc' : 'VATex'}`)
  },
}

export default function VatSelectionBar({ locale }: Readonly<{ locale: string }>) {
  const screen = useScreenClass()
  const isMobile = screen === 'xs'
  const { title, mobilePromptText, vatIncText, vatExcText } = vatBarData

  const { showVatSelectionBar, showMobilePrompt, handleVatSelection } = useVatSelectionBar(locale)

  if (!showVatSelectionBar) return null

  return (
    <FlexBox
      justifyContent="center"
      fontSkin="body-small"
      alignItems={{ xs: 'flex-start', sm: 'center' }}
      textColor="standard"
      darkMode
      gap={4}
      p={3}
      flexDirection={{ xs: 'column', sm: 'row' }}
      style={{ backgroundColor: getTokenCssVar('SwanSemColorBgStrong'), position: 'relative' }}
    >
      <FlexBox m={0} fontSkin="body-small-bold" flexWrap="wrap" style={{ whiteSpace: 'pre-line' }}>
        {showMobilePrompt ? mobilePromptText : title}
      </FlexBox>

      <FlexBox justifyContent="space-between" gap={{ xs: '3', sm: '4' }} alignItems="center" style={{ width: isMobile ? '100%' : 'auto' }}>
        {[vatExcText, vatIncText].map((text) => (
          <Button
            key={text}
            skin="secondary"
            py={3}
            px={6}
            width={isMobile ? 'full-width' : 'standard'}
            textColor="standard"
            fontSkin="body-small"
            onClick={() => handleVatSelection(text === vatIncText)}
          >
            {text}
          </Button>
        ))}
      </FlexBox>
    </FlexBox>
  )
}

const useVatSelectionBar = (locale: string) => {
  const [showMobilePrompt, setShowMobilePrompt] = useState(false)
  const [showVatSelectionBar, setShowVatSelectionBar] = useState(false)
  const { vatInclusive, setVatInclusively } = useVatContext()
  const screen = useScreenClass()
  const isMobile = screen === 'xs'

  const isValidPageSection = useMemo(() => {
    const { pageSection } = getPageInfo()
    return !!pageSection && !excludedPageSectionsForVatBar.includes(pageSection)
  }, [])

  useEffect(() => {
    const vatSelectionBarEnabled =
      vatBarEnabledLocales.includes(locale) &&
      !vatInclusive &&
      !getLocalStorageWithExpiry(LOCAL_STORAGE_KEYS.HIDE_VAT_SELECTION_BAR) &&
      !getCookie(COOKIE_KEYS.HAS_SEEN_VAT_BAR) &&
      isValidPageSection

    setShowVatSelectionBar(vatSelectionBarEnabled)
    if (vatSelectionBarEnabled) tracker.trackView(locale)
  }, [locale, vatInclusive])

  useEffect(() => {
    if (showVatSelectionBar && isValidPageSection && getCookie(COOKIE_KEYS.HAS_SEEN_VAT_BAR) !== 'true') {
      const isConsentPending = isCookieConsentPending()

      if (!isConsentPending) {
        setCookie(COOKIE_KEYS.HAS_SEEN_VAT_BAR, 'true')
      }
    }
  }, [locale, isValidPageSection, showVatSelectionBar])

  const handleVatSelection = (isVatInc: boolean) => {
    tracker.trackSelect(isVatInc)
    setLocalStorageWithExpiry(LOCAL_STORAGE_KEYS.HIDE_VAT_SELECTION_BAR, true, STORAGE_TTL.ONE_MONTH)
    setShowVatSelectionBar(false)
    setVatInclusively(isVatInc)
    if (isMobile) setShowMobilePrompt(true)
  }

  return { showVatSelectionBar, showMobilePrompt, handleVatSelection }
}
